// Dependencies
import { FC, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Stack } from '@mui/material';

// Context & APIs
import { AppContext } from '../context';
import { getDocument } from '../apis/pdf.apis';

// Pages
import { PDFViewer } from './PDFViewer';
import PDFViewerV2 from './PDFViewerV2';
import { isPrRealValueTemplate } from './PDFViewerV2/components/templates/pr-real-value/templateConstants';
import { clearSessionTokens, isSessionGoneError, loadErrorMessage } from './PDFViewerV2/loadErrors';

const PDFViewerRoute: FC = () => {
  const { id } = useParams<{ id: string }>();
  const { account, setAccount, setDocument } = useContext(AppContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [useV2Viewer, setUseV2Viewer] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    async function run() {
      if (!id) { setIsLoading(false); return; }
      try {
        setIsLoading(true);
        setLoadError(null);
        const doc = await getDocument(account?.id, id);
        if (!active) return;
        setDocument(doc);
        const tplType = doc?.template?.type;
        const tplId = doc?.template?._id;
        setUseV2Viewer(tplType === 'react-pdf' || isPrRealValueTemplate(tplId));
      } catch (e) {
        if (!active) return;
        // Swallowing this used to fall through to the V1 editor below — for a react-pdf
        // document that is the wrong editor with none of its fields, and its Save writes
        // V1 keys into the V2 document. Say why instead.
        console.error('[PDFViewerRoute] Factsheet konnte nicht geladen werden', { id, error: e });
        if (isSessionGoneError(e)) {
          clearSessionTokens();
          setAccount(null);
          return;
        }
        setLoadError(loadErrorMessage(e));
      } finally {
        if (active) setIsLoading(false);
      }
    }
    run();
    return () => { active = false; };
  }, [id, account?.id, setAccount, setDocument]);

  if (isLoading) {
    return (
      <Stack spacing={20} sx={{ alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ p: 64 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
              Neu laden
            </Button>
          }
        >
          Dieses Factsheet konnte nicht geladen werden ({loadError}). Es wurde nichts verändert.
        </Alert>
      </Box>
    );
  }

  if (useV2Viewer) {
    return <PDFViewerV2 />;
  }

  return <Box sx={{ p: 64 }}><PDFViewer /></Box>;
};

export default PDFViewerRoute;
